import React, { useEffect, useRef, useState } from "react";
import { TRoadblockChecklistItemType, TRoadblockType } from "../../../../../../domain/codecs/form/RoadblockForm";
import { capitalizeEveryWord } from "../../../../../../shared/src/utilsByDomain/string/capitalizeEveryWord";
import { underscoreCaseToNormalCase } from "../../../../../../shared/src/utilsByDomain/string";
import { CRMIcon } from "../../../CRMIcon/CRMIcon";
import { TDayWorkSource } from "../../../../../../domain/codecs/DayWorkSource";

export type TRoadblockDayViewFilterValueOf<TType extends string, TItem extends string> = {
    source: TDayWorkSource;
    roadblock_type: TType | null;
    checklist_item_type: TItem | null;
};

export type TRoadblockDayViewFilterValue = TRoadblockDayViewFilterValueOf<TRoadblockType, TRoadblockChecklistItemType>;

export type TChecklistItemOptionOf<TItem extends string> = {
    type: TItem;
    label: string;
};

// Generic over the two string unions so Sail Homes can drive the same two-tier dropdown
// with listing roadblocks. Defaults keep the Sail Legal call sites unchanged; Sail Homes
// passes its own configs and hides the source rows, having no tasks or key dates.
type TProps<TType extends string, TItem extends string> = {
    value: TRoadblockDayViewFilterValueOf<TType, TItem>;
    onChange: (value: TRoadblockDayViewFilterValueOf<TType, TItem>) => void;
    types: Array<TType>;
    getItemOptions: (roadblockType: TType) => Array<TChecklistItemOptionOf<TItem>>;
    showSourceOptions?: boolean;
};

const typeLabel = (roadblockType: string): string =>
    capitalizeEveryWord(underscoreCaseToNormalCase(roadblockType));

export const CRMDayRoadblockFilter = <TType extends string, TItem extends string>(props: TProps<TType, TItem>): JSX.Element => {
    const [isOpen, setIsOpen] = useState(false);
    const [drilledType, setDrilledType] = useState<TType | null>(null);

    const { types, getItemOptions } = props;
    const showSourceOptions = props.showSourceOptions !== false;

    const faceLabel = (value: TRoadblockDayViewFilterValueOf<TType, TItem>): string => {
        if (showSourceOptions && value.source === "tasks") {
            return "Tasks only";
        }
        if (showSourceOptions && value.source === "key_dates") {
            return "Key dates only";
        }
        if (showSourceOptions && value.source === "roadblocks" && value.roadblock_type === null) {
            return "Roadblocks only";
        }
        if (value.roadblock_type === null) {
            return "Any type";
        }
        if (value.checklist_item_type !== null) {
            const item = getItemOptions(value.roadblock_type)
                .find((option) => option.type === value.checklist_item_type);
            if (item) {
                return item.label;
            }
        }
        return typeLabel(value.roadblock_type);
    };
    const wrapRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!isOpen) {
            return;
        }
        const onDocMouseDown = (event: MouseEvent) => {
            if (
                wrapRef.current
                && event.target instanceof Node
                && !wrapRef.current.contains(event.target)
            ) {
                setIsOpen(false);
                setDrilledType(null);
            }
        };
        document.addEventListener("mousedown", onDocMouseDown);
        return () => document.removeEventListener("mousedown", onDocMouseDown);
    }, [isOpen]);

    const openPanel = () => {
        setDrilledType(null);
        setIsOpen((open) => !open);
    };

    const close = () => {
        setIsOpen(false);
        setDrilledType(null);
    };

    const selectAnyType = () => {
        props.onChange({ source: "any", roadblock_type: null, checklist_item_type: null });
        close();
    };

    const selectSource = (source: TDayWorkSource) => {
        props.onChange({ source, roadblock_type: null, checklist_item_type: null });
        close();
    };

    const selectType = (roadblockType: TType) => {
        props.onChange({ source: "roadblocks", roadblock_type: roadblockType, checklist_item_type: null });
        close();
    };

    const selectItem = (roadblockType: TType, itemType: TItem) => {
        props.onChange({ source: "roadblocks", roadblock_type: roadblockType, checklist_item_type: itemType });
        close();
    };

    return (
        <div className="crm-day-roadblock-filter" ref={wrapRef}>
            {/* FACE */}
            <div className="crm-day-roadblock-filter__face" onClick={openPanel} tabIndex={0}>
                <div className="crm-day-roadblock-filter__face-text">
                    {faceLabel(props.value)}
                </div>
                <CRMIcon iconName={isOpen ? "up-arrow" : "down-arrow"} colour="neutral-0" size="tiny" />
            </div>

            {/* PANEL */}
            {isOpen && (
                <div className="crm-day-roadblock-filter__panel">
                    <div className="crm-day-roadblock-filter__viewport">
                        <div className={`crm-day-roadblock-filter__slider ${drilledType !== null ? "crm-day-roadblock-filter__slider--tier-2" : ""}`}>

                            {/* TIER 1 — types */}
                            <div className="crm-day-roadblock-filter__tier">
                                <div
                                    className={`crm-day-roadblock-filter__option crm-day-roadblock-filter__option--any ${props.value.source === "any" ? "crm-day-roadblock-filter__option--selected" : ""}`}
                                    onClick={selectAnyType}
                                >
                                    <span className="crm-day-roadblock-filter__option-text">Any type</span>
                                </div>
                                {showSourceOptions && <>
                                    <div
                                        className={`crm-day-roadblock-filter__option ${props.value.source === "tasks" ? "crm-day-roadblock-filter__option--selected" : ""}`}
                                        onClick={() => selectSource("tasks")}
                                    >
                                        <span className="crm-day-roadblock-filter__option-text">Tasks only</span>
                                    </div>
                                    <div
                                        className={`crm-day-roadblock-filter__option ${props.value.source === "key_dates" ? "crm-day-roadblock-filter__option--selected" : ""}`}
                                        onClick={() => selectSource("key_dates")}
                                    >
                                        <span className="crm-day-roadblock-filter__option-text">Key dates only</span>
                                    </div>
                                    <div
                                        className={`crm-day-roadblock-filter__option ${props.value.source === "roadblocks" && props.value.roadblock_type === null ? "crm-day-roadblock-filter__option--selected" : ""}`}
                                        onClick={() => selectSource("roadblocks")}
                                    >
                                        <span className="crm-day-roadblock-filter__option-text">Roadblocks only</span>
                                    </div>
                                </>}
                                {types.map((roadblockType) => {
                                    const hasItems = getItemOptions(roadblockType).length > 0;
                                    const isSelected =
                                        props.value.roadblock_type === roadblockType
                                        && props.value.checklist_item_type === null
                                        && props.value.source === "roadblocks";
                                    return (
                                        <div
                                            key={roadblockType}
                                            className={`crm-day-roadblock-filter__option ${isSelected ? "crm-day-roadblock-filter__option--selected" : ""}`}
                                            onClick={() => selectType(roadblockType)}
                                        >
                                            <span className="crm-day-roadblock-filter__option-text">
                                                {typeLabel(roadblockType)}
                                            </span>
                                            {hasItems && (
                                                <span
                                                    className="crm-day-roadblock-filter__drill"
                                                    onClick={(event) => {
                                                        event.stopPropagation();
                                                        setDrilledType(roadblockType);
                                                    }}
                                                >
                                                    &rsaquo;
                                                </span>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>

                            {/* TIER 2 — checklist items for the drilled type */}
                            <div className="crm-day-roadblock-filter__tier">
                                {drilledType !== null && (
                                    <>
                                        <div className="crm-day-roadblock-filter__tier-2-head">
                                            <span
                                                className="crm-day-roadblock-filter__back"
                                                onClick={() => setDrilledType(null)}
                                            >
                                                <CRMIcon iconName="backward-arrow" colour="primary-0" size="tiny" />
                                                Back
                                            </span>
                                            <span className="crm-day-roadblock-filter__tier-2-title">
                                                {typeLabel(drilledType)}
                                            </span>
                                        </div>
                                        <div
                                            className={`crm-day-roadblock-filter__option crm-day-roadblock-filter__option--all ${props.value.roadblock_type === drilledType && props.value.checklist_item_type === null ? "crm-day-roadblock-filter__option--selected" : ""}`}
                                            onClick={() => selectType(drilledType)}
                                        >
                                            <span className="crm-day-roadblock-filter__option-text">
                                                All {typeLabel(drilledType)}
                                            </span>
                                        </div>
                                        {getItemOptions(drilledType).map((option) => (
                                            <div
                                                key={option.type}
                                                className={`crm-day-roadblock-filter__option ${props.value.checklist_item_type === option.type ? "crm-day-roadblock-filter__option--selected" : ""}`}
                                                onClick={() => selectItem(drilledType, option.type)}
                                            >
                                                <span className="crm-day-roadblock-filter__option-text">
                                                    {option.label}
                                                </span>
                                            </div>
                                        ))}
                                    </>
                                )}
                            </div>

                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
